import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Result } from 'antd';
import { Layout } from './components/Layout';
import { FiltreBarYuvasiSaglayici } from './components/FiltreBarYuvasi';
import { GenericReportPage } from './components/GenericReportPage';
import { DynamicDashboard } from './components/DynamicDashboard';
import { LoginPage } from './pages/Login';
import { ekranBileseni } from './ekranKayitDefteri';
import { useAuth } from './contexts/AuthContext';
import { usePanelConfig } from './hooks/usePanelConfig';
import type { ReactNode } from 'react';

function ProtectedShell({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Wait for AuthContext to finish restoring session from storage before
  // making redirect decisions — otherwise a returning user with a valid token
  // bounces to /login for one render before getMe resolves.
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Yükleniyor..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (location.pathname === '/login') return <>{children}</>;
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

function PanelShell() {
  const { data: config, isLoading, error } = usePanelConfig();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Yükleniyor..." />
      </div>
    );
  }

  if (error || !config) {
    return (
      <Result
        status="error"
        title="Panel yapılandırması yüklenemedi"
        subTitle="Lütfen sayfayı yenileyin veya destek ile iletişime geçin."
      />
    );
  }

  return (
    // G2b: Layout'un filtre bari yuvasi ile sayfanin portal'i ayni baglami paylasir.
    <FiltreBarYuvasiSaglayici>
      <Layout config={config}>
        <Routes>
          <Route
            path="/"
            element={
              <DynamicDashboard
                widgets={config.dashboard.widgets}
                reports={config.reports}
              />
            }
          />
          {config.reports.map((report) => (
            <Route
              key={report.code}
              path={`/rapor/${report.code}`}
              element={
                <GenericReportPage
                  report={report}
                  allReports={config.reports}
                  onNavigateReport={(code /* , paramValue — Step 6 will pass via search params */) => navigate(`/rapor/${code}`)}
                />
              }
            />
          ))}
          {config.screens?.map((screen) => {
            const Ekran = ekranBileseni(screen.key);
            return (
              <Route
                key={screen.key}
                path={`/${screen.key}`}
                element={<Ekran screen={screen} reports={config.reports} />}
              />
            );
          })}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </FiltreBarYuvasiSaglayici>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedShell>
            <PanelShell />
          </ProtectedShell>
        }
      />
    </Routes>
  );
}

export default App;
